/**
 * SierraCanvas
 *
 * SierraCanvas is a combined wrapper for HTMLCanvasElement and CanvasRenderingContext2D
 * that allows both to be accessed through the same variable, helping to keep code clean
 *
 * Create a new SierraCanvas instance
 * @example new SierraCanvas(width, height);
 */
import {configureScope, captureMessage, captureException} from '@sentry/vue';

import {
  hsvToRgb,
  isPercentage,
  parsePercentage,
  rgbToHex,
  rgbToHsv,
} from '@teemill/utilities';

import ColorGradient from './colorGradient';
import Vector from './vector';
import Logger from './logger';
import {v4 as uuid} from 'uuid';

import {isDeviceIos} from '@teemill/common/helpers';

const logger = new Logger('#507d2a', 'SierraCanvas');

const sierraCanvasWarningLimit = 100;

export class SierraCanvas {
  context;
  canvas;

  reserveKey;

  static defaultMaxCanvasSize = 16777216;

  // all major browsers support at least this size, except Safari for iOS
  maxCanvasSize = 124992400;
  // canvases cannot exceed 4k on Safari for iOS
  iosMaxCanvasSize = 16777216;

  static recordCanvasCreationsAndDeletions = false;
  static creationAndDeletionData = {};

  /**
   * Any HTMLCanvasElement or CanvasRenderingContext2D methods specified in this array
   * can be chained
   */
  chainableCommands = [
    'arc',
    'beginPath',
    'clearRect',
    'closePath',
    'drawImage',
    'fill',
    'fillRect',
    'fillText',
    'lineTo',
    'moveTo',
    'restore',
    'rect',
    'rotate',
    'save',
    'scale',
    'setLineDash',
    'stroke',
    'strokeRect',
    'strokeText',
    'transform',
    'translate',
    'cookieCut',
  ];

  /**
   * This is a global canvas intended for use when a disposable canvas is needed
   * This can be accessed via SierraCanvas.global
   */
  static _globalSierraCanvas;
  static canvasCount = 0;
  static _browserSupportsWebP;

  constructor(width = 100, height = 100, canvas) {
    if (!width || !height) {
      // force the canvas to be at least 1px wide and tall
      width = Math.max(1, width);
      height = Math.max(1, height);
    }

    this.width = width;
    this.height = height;

    if (isDeviceIos()) {
      this.maxCanvasSize = this.iosMaxCanvasSize;
    }

    this.createCanvas(canvas);

    // this adds magic methods to root through to the CanvasRenderingContext2D
    return this._proxySelf();
  }

  /**
   * SierraCanvas.global can be used to get a global, reusable canvas.
   * This is to prevent errors in certain browsers (Safari mobile) that can only handle
   * a certain number of canvases. After that, they can fail to create new canvases or
   * to get their contexts. This canvas should be used when a canvas is needed for
   * a brief period, such as to measure the width of text or to resize an image, after
   * which the canvas can be discarded
   */
  static get global() {
    if (!this._globalSierraCanvas) {
      this._globalSierraCanvas = new SierraCanvas(400, 600);
    }

    return this._globalSierraCanvas;
  }

  static fromCanvas(canvas) {
    return new SierraCanvas(canvas.width, canvas.height, canvas);
  }

  static browserSupportsWebP() {
    if (typeof SierraCanvas._browserSupportsWebP === 'undefined') {
      if (typeof window.chrome !== 'undefined') {
        // if Chrome, assume webp is supported
        SierraCanvas._browserSupportsWebP = true;
      } else if (typeof window.mozInnerScreenY !== 'undefined') {
        // if Firefox, assume webp is supported
        SierraCanvas._browserSupportsWebP = true;
      } else {
        // For all other browsers we can check if HTMLCanvas is capable of exporting
        // as image/webp. This isn't a 100% accurate test, as some browsers can load
        // .webp images but not export canvases as image/webp, so there may be
        // some false positives (this is why the Firefox test is performed above)
        const tempCnv = new SierraCanvas(1, 1);

        SierraCanvas._browserSupportsWebP =
          tempCnv.canvas?.toDataURL('image/webp')?.slice(0, 15) ===
          'data:image/webp';
      }
    }
    return SierraCanvas._browserSupportsWebP;
  }

  createCanvas(canvas) {
    this.increaseCanvasCount();

    this.uuid = uuid();

    if (!canvas) {
      this.canvas = document.createElement('canvas');
    } else {
      this.canvas = canvas;
    }
    this.context = this.canvas?.getContext?.('2d') ?? null;

    if (!this.context) {
      configureScope(scope => {
        scope.setLevel('error');
        scope.setExtra('canvasDimensions', {
          width: this.width,
          height: this.height,
        });
      });
      captureException(new Error('Failed to get 2D context from canvas.'));
    }

    if (SierraCanvas.recordCanvasCreationsAndDeletions) {
      SierraCanvas.creationAndDeletionData[this.uuid] = new Error().stack
        .split('\n')
        .map(value => {
          const [fullMatch, methodName, fileName, line, column] =
            value.match(
              /^([^@]*)@.*\/([a-zA-Z]*\.[jt]s).*:([0-9]*):([0-9]*)/
            ) ?? [];

          if (!methodName || !fileName) {
            return null;
          }

          return {
            method: methodName,
            file: fileName,
            line: line,
            column: column,
          };
        })
        .filter(value => value);
    }

    this.resizeCanvas(this.width, this.height);
  }

  /**
   * Resize the canvas to required dimensions
   * Note that changing the width or height of a canvas will reset all pixels to transparent
   *
   * @param {number} width The new width of the canvas
   * @param {number} height The new height of the canvas
   */
  resizeCanvas(width, height) {
    if (!width || !height) {
      // force the canvas to be at least 1px wide and tall
      width = Math.max(1, width);
      height = Math.max(1, height);
    }

    const pixelCount = width * height;
    // cannot exceed 4096 by 4096
    if (pixelCount > this.maxCanvasSize) {
      const ratio = pixelCount / this.maxCanvasSize;
      const sqrtOfRatio = Math.sqrt(ratio);

      const originalWidth = width;
      const originalHeight = height;

      width = Math.floor(width / sqrtOfRatio);
      height = Math.floor(height / sqrtOfRatio);

      this.context?.scale?.(sqrtOfRatio, 1 / sqrtOfRatio);
      this.context?.translate?.(
        (originalWidth - width) / 2,
        (originalHeight - height) / 2
      );
    }

    this.canvas.width = width;
    this.canvas.height = height;

    this.width = width;
    this.height = height;

    return this._proxySelf();
  }

  resizeToImage(image) {
    this.resizeCanvas(image.width, image.height);

    return this._proxySelf();
  }

  logSizeExceptionError(width, height) {
    configureScope(scope => {
      scope.setLevel('error');
      scope.setExtra('size', {
        width,
        height,
      });
    });

    captureMessage('Attempted to create a canvas of zero width or height');
  }

  /**
   * Check if context is available before using it
   * @private
   * @return {boolean}
   */
  _hasContext() {
    if (!this.context) {
      captureException(
        new Error(
          'Canvas context is not available. ' +
            'The canvas may have exceeded memory limits or the 2D context failed to initialize.'
        )
      );
      return false;
    }
    return true;
  }

  /**
   * Empty the canvas of all pixel data (reset to transparent)
   */
  clear() {
    this.context?.clearRect?.(0, 0, this.width, this.height);

    return this._proxySelf();
  }

  /**
   * Stroke (draw) a line from (x1, y1) to (x2, y2)
   *
   * @param {number} x1 The x coordinate of the first vector
   * @param {number} y1 The y coordinate of the first vector
   * @param {number} x2 The x coordinate of the second vector
   * @param {number} y2 The y coordinate of the second vector
   */
  strokeLine(x1, y1, x2, y2) {
    this.context?.beginPath?.();
    this.context?.moveTo?.(x1, y1);
    this.context?.lineTo?.(x2, y2);
    this.context?.stroke?.();
    this.context?.closePath?.();
  }

  /**
   * Setter function for image smoothing (anti-aliasing)
   *
   * @param {bool} enabled Whether or not to enable or disable image smoothing
   */
  setImageSmoothing(enabled) {
    if (this.context) {
      this.context.imageSmoothingEnabled = enabled;
    }

    return this._proxySelf();
  }

  /**
   * Setter function for composite operation (layer mixing)
   *
   * @param {string} composite Which composite operation to use
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/globalCompositeOperation
   */
  setCompositeOperation(composite) {
    if (this.context) {
      this.context.globalCompositeOperation = composite;
    }

    return this._proxySelf();
  }

  /**
   * Setter function for global alpha
   *
   * @param {float} alpha A number between 0.0 and 1.0 for the alpha (opacity) of future operations
   */
  setGlobalAlpha(alpha) {
    if (this.context) {
      this.context.globalAlpha = alpha;
    }

    return this._proxySelf();
  }

  /**
   * Setter function for fill style
   *
   * @param {string} fillStyle A CSS compatible colour string e.g. '#000', 'rgb(0,0,0)', 'black', etc.
   */
  setFillStyle(fillStyle) {
    if (this.context) {
      this.context.fillStyle = fillStyle;
    }

    return this._proxySelf();
  }

  /**
   * Setter function for stroke style
   *
   * @param {string} strokeStyle A CSS compatible colour string e.g. '#000', 'rgb(0,0,0)', 'black', etc.
   */
  setStrokeStyle(strokeStyle) {
    if (this.context) {
      this.context.strokeStyle = strokeStyle;
    }

    return this._proxySelf();
  }

  /**
   * Setter function for lineWidth
   *
   * @param {number} lineWidth width of the line in pixels
   */
  setLineWidth(lineWidth) {
    if (this.context) {
      this.context.lineWidth = lineWidth;
    }

    return this._proxySelf();
  }

  forEachPixel(callback, x, y, w, h, passive = false, alphaOnly = false) {
    const imageData = this.context?.getImageData?.(
      x || 0,
      y || 0,
      w || this.width,
      h || this.height
    );

    if (!imageData) {
      return;
    }

    const data = imageData.data;
    const l = data.length;
    let xCoord = 0;
    let yCoord = 0;
    let pixelData = {};

    for (let i = 0; i < l; i += 4) {
      xCoord += 1;
      if (xCoord > (w || this.width)) {
        xCoord = 1;
        yCoord += 1;
      }

      if (alphaOnly) {
        pixelData = {
          a: data[i + 3],
        };
      } else {
        pixelData = {
          r: data[i],
          g: data[i + 1],
          b: data[i + 2],
          a: data[i + 3],
        };
      }

      callback(pixelData, xCoord, yCoord);

      if (!passive) {
        if (!alphaOnly) {
          data[i] = pixelData.r;
          data[i + 1] = pixelData.g;
          data[i + 2] = pixelData.b;
        }
        data[i + 3] = pixelData.a;
      }
    }

    if (!passive) {
      this.context?.putImageData?.(imageData, x || 0, y || 0);
    }
  }

  detectUniqueColours(x = null, y = null, w = null, h = null) {
    const colours = {};
    this.forEachPixel(
      pixelData => {
        if (pixelData.a > 0.8) {
          const col = `${pixelData.r}|${pixelData.g}|${pixelData.b}`;

          if (colours[col]) {
            colours[col] += 1;
          } else {
            colours[col] = 1;
          }
        }
      },
      x,
      y,
      w,
      h
    );

    return colours;
  }

  countUniqueColours(x = null, y = null, w = null, h = null) {
    return Object.keys(this.detectUniqueColours(x, y, w, h)).length;
  }

  getAverageColour(format = 'rgb') {
    const cnv = new SierraCanvas();
    let col = {};
    cnv
      .resizeCanvas(1, 1)
      .drawImage(this.canvas, 0, 0, 1, 1)
      .forEachPixel(pixelData => {
        col = pixelData;
      });

    cnv.destroy();

    if (format === 'hsv') {
      return rgbToHsv(col);
    }

    if (format === 'hex') {
      return rgbToHex(col);
    }

    return col;
  }

  tileImage(image, x = 0, y = 0, sw = null, sh = null, w = null, h = null) {
    if (!sw) {
      sw = image.width;
    }

    if (!sh) {
      sh = image.height;
    }

    if (!w) {
      w = this.width;
    }

    if (!h) {
      h = this.height;
    }

    const widthTileCount = Math.ceil(w / sw);
    const heightTileCount = Math.ceil(h / sh);

    for (let tileX = 0; tileX < widthTileCount; ++tileX) {
      for (let tileY = 0; tileY < heightTileCount; ++tileY) {
        this.drawImage(image, x + sw * tileX, y + sh * tileY, sw, sh);
      }
    }

    return this._proxySelf();
  }

  /**
   * Apply a composite operation to the entire canvas
   *
   * @param {string} operation  The name of the globalCompositeOperation to use
   * @param {string} colour     A CSS compatible colour string e.g. '#000', 'rgb(0,0,0)', 'black', etc.
   * @param {number} strength   The strength which which to apply the chosen composite operation.
   *                            Typically a value between 0 and 1, where 0 is not at all and 1 is to
   *                            the maximum
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/globalCompositeOperation
   */

  applyCompositeOperation(operation, colour, strength) {
    const tmpCanvas = SierraCanvas.global;
    tmpCanvas
      .resizeCanvas(this.width, this.height)
      .drawImage(this.canvas, 0, 0);

    this.save().setCompositeOperation(operation).setFillStyle(colour);

    const loops = Math.floor(strength);

    for (let i = 0; i < loops; ++i) {
      this.fillRect(0, 0, this.width, this.height);
    }

    this.setGlobalAlpha(strength - loops)
      .fillRect(0, 0, this.width, this.height)
      .restore()
      .cookieCut(tmpCanvas.canvas);
  }

  /**
   * Apply a cookie cutter style crop to the canvas. Any pixels that are transparent in the template
   * image will be removed from the canvas
   *
   * @param {HTMLImagElement|HTMLCanvasElement} image The image to use as the cookie cutter
   */
  cookieCut(image, x = 0, y = 0, w = null, h = null, invert = false) {
    if (typeof x === 'string') {
      if (isPercentage(x)) {
        x = this.width * (parsePercentage(x) / 100);
      }
    }
    if (typeof y === 'string') {
      if (isPercentage(y)) {
        y = this.height * (parsePercentage(y) / 100);
      }
    }
    if (typeof w === 'string') {
      if (isPercentage(w)) {
        w = this.width * (parsePercentage(w) / 100);
      }
    }
    if (typeof h === 'string') {
      if (isPercentage(h)) {
        h = this.height * (parsePercentage(h) / 100);
      }
    }

    this.save()
      .setCompositeOperation(invert ? 'destination-out' : 'destination-in')
      .setGlobalAlpha(1);

    if (w && h) {
      this.drawImage(image, x, y, w, h);
    } else {
      this.drawImage(image, x, y);
    }

    this.restore();

    return this._proxySelf();
  }

  /**
   * Apply a cookie cutter style crop to the canvas. Any pixels that are transparent in the template
   * image will be removed from the canvas
   *
   * @param {HTMLImagElement|HTMLCanvasElement} image The image to use as the cookie cutter
   */
  reverseCookieCut(image, x = 0, y = 0, w = null, h = null) {
    this.cookieCut(image, x, y, w, h, true);
  }

  saturation(strength) {
    if (strength > 0) {
      this.oversaturate(strength);
    } else {
      this.desaturate(Math.abs(strength));
    }
  }

  /**
   * Desaturate the canvas
   *
   * @param {number} strength The strength of desaturation (0 being full saturation)
   */
  desaturate(strength) {
    this.applyCompositeOperation('saturation', '#000', strength);
  }
  /**
   * Desaturate the canvas
   *
   * @param {number} strength The strength of desaturation (0 being full saturation)
   */
  oversaturate(strength) {
    this.applyCompositeOperation('saturation', '#f00', strength);
  }

  brightness(strength) {
    if (strength >= 0) {
      this.brighten(strength);
    } else {
      this.darken(Math.abs(strength));
    }
  }

  /**
   * Brighten the entire canvas
   *
   * @param {number} strength The strength of brightness (0 being no difference,
   *                          1 making everything white)
   */
  brighten(strength) {
    this.applyCompositeOperation('screen', '#fff', strength);
  }

  /**
   * Darken the entire canvas
   *
   * @param {number} strength The strength of darkening (0 being no difference,
   *                          1 making everything black)
   */
  darken(strength) {
    this.applyCompositeOperation('multiply', '#000', strength);
  }

  /**
   * Brighten the entire canvas, but using luminance rather than raw brightness. This
   * should make the image brighter in ways that seem more natural to the human eye
   *
   * @param {number} strength The strength of luminance (0 being no difference,
   *                          1 making everything white)
   */
  increaseLuminance(strength) {
    this.applyCompositeOperation('luminosity', '#fff', strength);
  }

  /**
   * Darken the entire canvas, but using luminance rather than raw brightness. This
   * should make the image darker in ways that seem more natural to the human eye
   *
   * @param {number} strength The strength of de-luminance (0 being no difference,
   *                          1 making everything black)
   */
  decreaseLuminance(strength) {
    this.applyCompositeOperation('luminosity', '#000', strength);
  }

  luminance(strength) {
    if (strength >= 0) {
      this.increaseLuminance(strength);
    } else {
      this.decreaseLuminance(Math.abs(strength));
    }
  }

  /**
   * Increase the warmth of the image, shifting the colours in the image to a more
   * orange-red
   *
   * @param {number} strength The strength of the warming, 0 being no difference, 1
   *                          making everything a very strong orange-red
   */
  warmUp(strength) {
    this.applyCompositeOperation('color', '#f4511e', strength);
  }

  /**
   * Decrease the warmth of the image, shifting the colours in the image to a more
   * ice-blue
   *
   * @param {number} strength The strength of the cooling, 0 being no difference, 1
   *                          making everything a very sharp ice-blue
   */
  coolDown(strength) {
    this.applyCompositeOperation('color', '#81d4fa', strength);
  }

  /**
   * Increase the darkness of dark areas of the image
   *
   * @param {number} strength The strength of the shadows, 0 being no difference, 1
   *                          making the shadows all black or near-black
   */
  shadows(strength) {
    this.applyCompositeOperation('overlay', '#000', strength);
  }

  /**
   * Increase the brightness of light areas of the image
   *
   * @param {number} strength The strength of the highlights, 0 being no difference, 1
   *                          making the highlights all white or near-white
   */
  highlights(strength) {
    this.applyCompositeOperation('overlay', '#fff', strength);
  }

  /**
   * Tints the image with a specified colour
   *
   * @param {number} strength The strength of the colour, 0 being no difference, 1
   *                          being a strong tint
   */
  colourTint(colour, strength) {
    this.applyCompositeOperation('soft-light', colour, strength);
  }

  /**
   * Fills the image with a specified colour
   */
  colourFill(colour, dx = 0, dy = 0, dWidth = 0, dHeight = 0) {
    this.setFillStyle(colour);

    if (!dWidth) {
      dWidth = this.width;
    }
    if (!dHeight) {
      dHeight = this.height;
    }

    this.fillRect(dx, dy, dWidth, dHeight);
  }

  hueFill(colour, strength) {
    this.applyCompositeOperation('hue', colour, strength);
  }

  gradientTint(
    stops,
    strength,
    x0 = 0,
    y0 = 0,
    x1 = 100,
    y1 = 100,
    dx = 0,
    dy = 0,
    dWidth = 0,
    dHeight = 0
  ) {
    this.gradientFill(
      stops,
      x0,
      y0,
      x1,
      y1,
      dx,
      dy,
      dWidth,
      dHeight,
      'tint',
      strength
    );
  }

  gradientFill(
    stops,
    x0 = 0,
    y0 = 0,
    x1 = 100,
    y1 = 100,
    dx = 0,
    dy = 0,
    dWidth = 0,
    dHeight = 0,
    mode = 'fill',
    strength = 0
  ) {
    if (!dWidth) {
      dWidth = this.width;
    }

    if (!dHeight) {
      dHeight = this.height;
    }

    let gradient = this.createLinearGradient(
      (dWidth / 100) * x0 + dx,
      (dHeight / 100) * y0 + dy,
      (dWidth / 100) * x1 + dx,
      (dHeight / 100) * y1 + dy
    );

    if (stops instanceof ColorGradient) {
      gradient = stops.toCanvasGradient(gradient, true);
    } else {
      Object.keys(stops).forEach(index => {
        gradient.addColorStop(index, stops[index]);
      });
    }

    switch (mode) {
      case 'tint':
        this.colourTint(gradient, strength, dx, dy, dWidth, dHeight);
        break;
      case 'fill':
      default:
        this.colourFill(gradient, dx, dy, dWidth, dHeight);
        break;
    }
  }

  /**
   * Removes white and very light pixels from an image
   *
   * @param {Float} sensitivity How bright does a pixel need to be to be considered
   * 'white'? 1 = white, 0 = black
   */
  stripWhite(sensitivity) {
    if (!sensitivity) {
      sensitivity = 0.9;
    }

    const offscreenCanvas = SierraCanvas.global;

    offscreenCanvas
      .resizeCanvas(this.width, this.height)
      .drawImage(this.canvas, 0, 0, this.width, this.height);

    let imgData = false;

    /**
     * This try/catch exists to help debug Sentry issue DASHBOARD-NP
     * @see https://sentry.io/organizations/teemill-tech-ltd/issues/915712852/
     */
    try {
      imgData = offscreenCanvas.getImageData(0, 0, this.width, this.height);
    } catch (error) {
      configureScope(scope => {
        scope.setExtra('width', this.width);
        scope.setExtra('height', this.height);
      });
      captureException(error);

      // fail nicely
      return;
    }

    const d = imgData.data;

    for (let i = 0, l = d.length; i < l; i += 4) {
      const lum = (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]) / 255;
      const rgbRange =
        Math.max(d[i], d[i + 1], d[i + 2]) - Math.min(d[i], d[i + 1], d[i + 2]);
      if (lum > sensitivity && rgbRange < 20) {
        d[i + 3] *= (lum - sensitivity) / (1 - sensitivity);
      } else {
        d[i + 3] = 0;
      }
    }

    offscreenCanvas.putImageData(imgData, 0, 0);
    this.save()
      .setCompositeOperation('destination-out')
      .drawImage(offscreenCanvas.canvas, 0, 0)
      .setGlobalAlpha(0.66)
      .drawImage(offscreenCanvas.canvas, -1, -1)
      .drawImage(offscreenCanvas.canvas, -1, 1)
      .drawImage(offscreenCanvas.canvas, 1, 1)
      .drawImage(offscreenCanvas.canvas, 1, -1)
      .restore();
  }

  angularGradientFill(stops, angle, dx = 0, dy = 0, dWidth = 0, dHeight = 0) {
    const vector = new Vector().fromAngle(angle - 90);
    let x0 = 0;
    let y0 = 0;
    let x1 = 100;
    let y1 = 100;

    if (vector.x > 0) {
      x0 = 0;
      x1 = vector.x * 100;
    } else {
      x0 = 100;
      x1 = 100 + vector.x * 100;
    }

    if (vector.y > 0) {
      y0 = 0;
      y1 = vector.y * 100;
    } else {
      y0 = 100;
      y1 = 100 + vector.y * 100;
    }

    this.gradientFill(stops, x0, y0, x1, y1, dx, dy, dWidth, dHeight);
  }

  /**
   * Decrease the opacity of the image
   *
   * @param {number} strength The new opacity. The opacity of the image will be
   *                          multiplied by this amount
   */
  opacity(strength) {
    const tmpCanvas = SierraCanvas.global;
    tmpCanvas
      .resizeCanvas(this.width, this.height)
      .drawImage(this.canvas, 0, 0);

    this.save()
      .setGlobalAlpha(strength)
      .clear()
      .drawImage(tmpCanvas.canvas, 0, 0)
      .restore();
  }

  /**
   * Recolour an image to a specified RGB
   *
   * @param {object} rgb  The new colour. All pixels in the image will be shifted to
   *                      the hue of this colour. The saturation and value will be
   *                      unaffected (assuming mode is not specified)
   * @param {string} mode Define the globalCompositeOperation to use for this filter
   *                      This is 'hue' by default. Changing this mode can
   *                      significantly affect how this filter behaves
   */
  recolour(rgb, mode, strength = 1) {
    const tmpCanvas = SierraCanvas.global;
    tmpCanvas
      .resizeCanvas(this.width, this.height)
      .drawImage(this.canvas, 0, 0);

    this.save()
      .setCompositeOperation(mode || 'hue')
      .setGlobalAlpha(strength)
      .setFillStyle(`rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`)
      .fillRect(0, 0, this.width, this.height)
      .setGlobalAlpha(1)
      .setCompositeOperation('destination-over')
      .drawImage(tmpCanvas.canvas, 0, 0)
      .setCompositeOperation('destination-in')
      .drawImage(tmpCanvas.canvas, 0, 0)
      .restore();
  }

  modifyHsv(hsv, mode, strength = 1) {
    hsv = Object.assign(
      {
        h: false,
        s: 1,
        v: 1,
      },
      hsv
    );
    const rgb = hsvToRgb(hsv);

    this.recolour(rgb, mode, strength);
  }

  blackToAlpha() {
    this.forEachPixel(pixel => {
      pixel.a = (pixel.r + pixel.g + pixel.b) / 3;
    });
  }

  vignette(innerRadius = 50, outerRadius = 100, opacity = 1, exponent = 1.2) {
    const tmpCanvas = new SierraCanvas();
    tmpCanvas
      .resizeCanvas(this.width, this.height)
      .drawImage(this.canvas, 0, 0);

    const distanceToCorner = Math.sqrt(this.width ** 2 + this.height ** 2) / 2;

    const grad = this.createRadialGradient(
      this.width / 2,
      this.height / 2,
      distanceToCorner * (innerRadius / 100),
      this.width / 2,
      this.height / 2,
      distanceToCorner * (outerRadius / 100)
    );

    for (let i = 0.05; i <= 1; i += 0.05) {
      grad.addColorStop(
        i,
        rgbToHex({
          r: 0,
          g: 0,
          b: 0,
          a: opacity * i ** exponent,
        })
      );
    }

    this.save()
      .setFillStyle(grad)
      .fillRect(0, 0, this.width, this.height)
      .restore();

    this.cookieCut(tmpCanvas.canvas);

    tmpCanvas.destroy();
  }

  /**
   * This is a middle-man function for the native CanvasRenderingContext2D
   * createLinearGradient method. This checks that all parameters are valid, to
   * handle invalid inputs and prevent the canvas from crashing.
   *
   * @param  {...any} args An array of coordinates. x0, y0, x1, y1
   * @see https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/createLinearGradient
   */
  createLinearGradient(...args) {
    for (let i = 0, l = args.length; i < l; ++i) {
      let num = args[i];

      // if the value is not a number or is a non-finite number, it is invalid
      // and we assume default behaviour
      if (isNaN(num) || !isFinite(num)) {
        // if this is x0 or y0, default to 0
        if (i < 2) {
          num = 0;
        }

        // if this is x1 or y1, default to the canvas width or height
        if (i % 2) {
          num = this.canvas.width;
        } else {
          num = this.canvas.height;
        }

        args[i] = num;
      }
    }

    return this.context?.createLinearGradient?.(...args);
  }

  copy(width = 'auto', height = 'auto', x = 0, y = 0, w = 0, h = 0) {
    const cnv = new SierraCanvas();

    if (width === 'auto' && height === 'auto') {
      width = this.width;
      height = this.height;
    } else if (width === 'auto') {
      width = (height / this.height) * this.width;
    } else if (height === 'auto') {
      height = (width / this.width) * this.height;
    }

    if (typeof width !== 'number' || typeof height !== 'number') {
      throw new Error('Invalid width or height in SierraCanvas.copy');
    }

    cnv.resizeCanvas(width, height);

    if (x || y || w || h) {
      cnv.drawImage(this.canvas, x, y, w, h, 0, 0, width, height);
    } else {
      cnv.drawImage(this.canvas, 0, 0, width, height);
    }

    return cnv;
  }

  createScaledCopy(scale, x = 0, y = 0, w = 0, h = 0) {
    return this.copy(this.width * scale, this.height * scale, x, y, w, h);
  }

  /**
   * Removes whitespace from the canvas
   *
   * @param {Number} accuracy Lower values are faster, higher values are more accurate
   * @param {Number} padding Add padding to prevent trimming content
   */
  trim(accuracy = 0.5, padding = 10) {
    const scale = 1 / accuracy;

    const globalCanvas = SierraCanvas.global;
    globalCanvas.resizeToImage(this.canvas).drawImage(this.canvas, 0, 0);

    const originalWidth = this.width;
    const originalHeight = this.height;

    this.resizeCanvas(
      Math.floor(this.width * accuracy),
      Math.floor(this.height * accuracy)
    ).drawImage(globalCanvas.canvas, 0, 0, this.width, this.height);

    const bounds = this.getActivePixelBounds();

    // by default we want to add padding in all 4 axis
    let xPadding = padding * 2;
    let yPadding = padding * 2;

    // but if the content of the image is against the right or bottom edge
    // then we only add the padding to the left and top
    if ((bounds.x + bounds.w) * scale === originalWidth) {
      xPadding = padding;
    }
    if ((bounds.y + bounds.h) * scale === originalHeight) {
      yPadding = padding;
    }

    const newWidth = Math.min(globalCanvas.width, bounds.w * scale + xPadding);

    const newHeight = Math.min(
      globalCanvas.height,
      bounds.h * scale + yPadding
    );

    const xOffset = Math.max(0, bounds.x * scale - padding);
    const yOffset = Math.max(0, bounds.y * scale - padding);

    this.resizeCanvas(newWidth, newHeight);

    this.drawImage(
      globalCanvas.canvas,
      xOffset,
      yOffset,
      Math.min(newWidth, originalWidth - xOffset),
      Math.min(newHeight, originalHeight - yOffset),
      0,
      0,
      this.width,
      this.height
    );

    return {
      x: bounds.x * scale,
      y: bounds.y * scale,
      w: bounds.w * scale,
      h: bounds.h * scale,
    };
  }

  getActivePixelBounds() {
    const cnv = new SierraCanvas();

    cnv.resizeCanvas(this.width, this.height).drawImage(this.canvas, 0, 0);

    let minX = cnv.width;
    let maxX = 0;
    let minY = cnv.height;
    let maxY = 0;

    cnv.forEachPixel(
      (pixel, x, y) => {
        if (pixel.a) {
          if (x < minX) {
            minX = x;
          } else if (x > maxX) {
            maxX = x;
          }
          if (y < minY) {
            minY = y;
          } else if (y > maxY) {
            maxY = y;
          }
        }
      },
      0,
      0,
      cnv.width,
      cnv.height,
      true,
      true
    );

    cnv.destroy();

    return {
      x: Math.floor(minX),
      y: Math.floor(minY),
      w: Math.ceil(maxX) - Math.floor(minX),
      h: Math.ceil(maxY) - Math.floor(minY),
    };
  }

  drawImage(img, dx = 0, dy = 0, dWidth, dHeight, sx, sy, sWidth, sHeight) {
    if (
      img instanceof SierraCanvas ||
      img.canvas instanceof HTMLCanvasElement
    ) {
      img = img.canvas;
    }

    if (sx || sy || sWidth || sHeight) {
      // Certain browsers will render a blank canvas if these parameters
      // are out of range of the pixel dimensions of the source image
      // so here we can clamp it to within range
      if (dWidth > img.width - dx) {
        dWidth = img.width - dx;
      }

      if (dHeight > img.height - dy) {
        dHeight = img.height - dy;
      }

      this.context?.drawImage?.(
        img,
        Math.round(dx),
        Math.round(dy),
        Math.round(dWidth),
        Math.round(dHeight),
        Math.round(sx),
        Math.round(sy),
        Math.round(sWidth),
        Math.round(sHeight)
      );
    } else if (dWidth || dHeight) {
      this.context?.drawImage?.(
        img,
        Math.round(dx),
        Math.round(dy),
        Math.round(dWidth),
        Math.round(dHeight)
      );
    } else {
      this.context?.drawImage?.(img, Math.round(dx), Math.round(dy));
    }

    return this._proxySelf();
  }

  drawImageToCover(image, x, y, w, h) {
    const imageAspectRatio = image.width / image.height;
    const targetAspectRatio = w / h;
    let offsetX = 0;
    let offsetY = 0;
    let offsetWidth = 0;
    let offsetHeight = 0;

    if (imageAspectRatio > targetAspectRatio) {
      // image is wider than target
      const scaledWidth = (image.width / image.height) * h;
      offsetX = (scaledWidth - w) / 2;
      offsetHeight = image.height;
      offsetWidth = offsetHeight * (w / h);
    } else {
      // image is taller than target
      const scaledHeight = (image.height / image.width) * w;
      offsetY = (scaledHeight - h) / 2;
      offsetWidth = image.width;
      offsetHeight = offsetWidth * (h / w);
    }

    this.drawImage(
      image,
      offsetX,
      offsetY,
      offsetWidth,
      offsetHeight,
      x,
      y,
      w,
      h
    );
  }

  drawImageToContain(
    image,
    x,
    y,
    targetWidth,
    targetHeight,
    centerX = true,
    centerY = true
  ) {
    const imageAspectRatio = image.width / image.height;
    const targetAspectRatio = targetWidth / targetHeight;

    let finalWidth = targetWidth;
    let finalHeight = targetHeight;

    if (imageAspectRatio > targetAspectRatio) {
      // image is wider than target
      finalHeight = (targetWidth / image.width) * image.height;
    } else {
      // image is taller than target
      finalWidth = (targetHeight / image.height) * image.width;
    }

    if (centerX) {
      x += (targetWidth - finalWidth) / 2;
    }

    if (centerY) {
      y += (targetHeight - finalHeight) / 2;
    }

    this.drawImage(image, x, y, finalWidth, finalHeight);
  }

  /**
   * A wrapper to set all shadow options on a canvas at once
   *
   * @param {int} offsetX how many pixels to shift the shadow in the x axis
   * @param {int} offsetY how many pixels to shift the shadow in the x axis
   * @param {int} blur how many pixels to blur the shadow by
   * @param {string} color A CSS compatible color string. e.g. a hex or rgb string
   */
  setShadow(offsetX, offsetY, blur, color) {
    this.context.shadowColor = color;
    this.context.shadowBlur = blur;
    this.context.shadowOffsetX = offsetX;
    this.context.shadowOffsetY = offsetY;

    return this._proxySelf();
  }

  /**
   * Scales the canvas from a specified focal point
   *
   * @param {float} scaleX How much to scale in the X axis
   * @param {float} scaleY How much to scale in the Y axis
   * @param {float} originX The X position to scale from
   * @param {float} originY The Y position to scale from
   */
  scaleFromPosition(scaleX, scaleY, originX, originY) {
    this.translate(originX, originY)
      .scale(scaleX, scaleY)
      .translate(-originX, -originY);

    return this._proxySelf();
  }

  rotateAroundPoint(angle, originX, originY) {
    this.translate(originX, originY)
      .rotate(angle)
      .translate(-originX, -originY);

    return this._proxySelf();
  }

  /**
   * This is a private method used internally for chaining functions
   */
  _proxySelf() {
    return new Proxy(this, {
      get: (sierraCanvas, field) => {
        try {
          if (field in sierraCanvas) {
            return sierraCanvas[field];
          }

          // some browsers need the context to be refreshed from time to time
          if (!(sierraCanvas.context instanceof CanvasRenderingContext2D)) {
            sierraCanvas.context = sierraCanvas.canvas.getContext('2d');
          }

          if (!(sierraCanvas.context instanceof CanvasRenderingContext2D)) {
            // if the browser still hasn't managed to find the context
            // then there is nothing more we can do at this point except prevent
            // the canvas from becoming corrupt
            const fieldValue =
              typeof field === 'string' ? field : field.toString();

            logger.log(
              `Unable to retrieve sierraCanvas context: ${fieldValue}`,
              logger.flags.ERROR
            );

            if (sierraCanvas.chainableCommands.includes(field)) {
              return () => sierraCanvas._proxySelf();
            }

            if (field in sierraCanvas.canvas) {
              if (typeof sierraCanvas.canvas[field] === 'function') {
                return (...args) =>
                  sierraCanvas.chainContextFunction(field, ...args);
              }

              return sierraCanvas.canvas[field];
            }

            return null;
          }

          if (field in sierraCanvas.context) {
            if (typeof sierraCanvas.context[field] === 'function') {
              return (...args) =>
                sierraCanvas.chainContextFunction(field, ...args);
              // return (...args) => sierraCanvas.context[field](...args);
            }

            return sierraCanvas.context[field];
          }

          if (field in sierraCanvas.canvas) {
            if (typeof sierraCanvas.canvas[field] === 'function') {
              return (...args) =>
                sierraCanvas.chainContextFunction(field, ...args);
              // return (...args) => sierraCanvas.context[field](...args);
            }

            return sierraCanvas.canvas[field];
          }

          return sierraCanvas[field];
        } catch (error) {
          configureScope(scope => {
            scope.setLevel('error');
            scope.setExtra('Failed to chain function in SierraCanvas', {
              canvasCount: SierraCanvas.canvasCount,
            });
          });

          captureException(error);
        }

        return false;
      },

      set: (sierraCanvas, field, value) => {
        if (field in sierraCanvas) {
          sierraCanvas[field] = value;
        }

        if (sierraCanvas.context && field in sierraCanvas.context) {
          if (typeof sierraCanvas.context[field] === 'function') {
            // eslint-disable-next-line
            console.error(`Cannot redefine method ${field}`);
          }

          sierraCanvas.context[field] = value;
        }

        if (field in sierraCanvas.canvas) {
          if (typeof sierraCanvas.canvas[field] === 'function') {
            // eslint-disable-next-line
            console.error(`Cannot redefine method ${field}`);
          }

          sierraCanvas.canvas[field] = value;
        }

        sierraCanvas[field] = value;
        return true;
      },
    });
  }

  chainContextFunction(field, ...args) {
    const contextFunction = this.context?.[field];
    const canvasFunction = this.canvas?.[field];

    if (this.chainableCommands.includes(field)) {
      if (typeof contextFunction === 'function') {
        contextFunction.call(this.context, ...args);
      } else if (typeof canvasFunction === 'function') {
        canvasFunction.call(this.canvas, ...args);
      }
      return this._proxySelf();
    }

    if (typeof contextFunction === 'function') {
      return contextFunction.call(this.context, ...args);
    }

    if (typeof canvasFunction === 'function') {
      return canvasFunction.call(this.canvas, ...args);
    }

    return null;
  }

  toImage(format = 'image/png', quality = 0.92) {
    return new Promise(resolve => {
      const img = new Image();
      // Note: do NOT set crossOrigin here — the src is a data URL, which does
      // not support CORS. Setting crossOrigin on a data-URL image causes Chrome
      // to mark it as tainted, which prevents createImageBitmap() from producing
      // a transferable bitmap (DataCloneError on postMessage — Sentry DASHBOARD-N3P).
      img.onload = () => {
        resolve(img);
      };
      img.src = this.toDataURL(format, quality);
    });
  }

  /**
   * This is the same as toImage, except it will wait for browser idle time to reduce the performance impact as converting
   * a canvas to an image can be quite performance intensive for larger files. The trade off is that this will take longer
   *
   * @param {int} fallbackTimeout When requestIdleCallback isn't supported, fallback to a timeout of this many milliseconds.
   * By default this value is set to 16 which is the length of 1 frame @ 60 fps
   * @param {string} format The mimetype to be used. One of image/jpeg, image/webp or image/png (default)
   * @param {float} quality A quality value between 0 and 1. Defaults to 0.92. Higher values result in larger file sizes.
   * Lower values have more compression artifacts and loss of quality. This value has no effect on image/png files
   *
   * @returns Promise<Image>
   */
  toImageOnIdle(fallbackTimeout = 16, format = 'image/png', quality = 0.92) {
    return new Promise(resolve => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() =>
          this.toImage(format, quality).then(img => {
            resolve(img);
          })
        );
      } else {
        setTimeout(() => {
          this.toImage().then(img => resolve(img));
        }, fallbackTimeout);
      }
    });
  }

  when(condition, callback) {
    if (condition) {
      callback(this._proxySelf());
    }

    return this._proxySelf();
  }

  destroy(key) {
    if (this.reserveKey && key !== this.reserveKey) {
      return false;
    }

    this.reserveKey = null;
    this.resizeCanvas(1, 1);

    this.decreaseCanvasCount();

    return true;
  }

  reserve() {
    this.reserveKey = Date.now();

    return this.reserveKey;
  }

  increaseCanvasCount() {
    SierraCanvas.canvasCount += 1;
  }

  decreaseCanvasCount() {
    SierraCanvas.canvasCount -= 1;

    if (SierraCanvas.recordCanvasCreationsAndDeletions) {
      delete SierraCanvas.creationAndDeletionData[this.uuid];
    }
  }

  /**
   * Any canvases that are created after this is called will be added to the
   * creationAndDeletionData array. When they are destroyed, they are removed
   * from that array.
   *
   * Calling endLeakTest will return an array detailing all canvases that were
   * created without being destroyed between those two funciton calls, and where
   * they came from.
   */
  static beginLeakTest() {
    SierraCanvas.recordCanvasCreationsAndDeletions = true;
  }

  static endLeakTest() {
    const leakedCanvases = Object.values(SierraCanvas.creationAndDeletionData);
    SierraCanvas.creationAndDeletionData = {};

    SierraCanvas.recordCanvasCreationsAndDeletions = false;

    return leakedCanvases;
  }
}

if (!import.meta.env.PROD) {
  // expose SierraCanvas to the window in non-production environments for debugging
  window._SierraCanvas = new Proxy(SierraCanvas, {
    get: (sierraCanvas, field) => {
      console.warn(
        "SierraCanvas accessed via global window property. Be aware this property doesn't exist in production and should not be relied upon."
      );
      return sierraCanvas[field];
    },
    set: (sierraCanvas, field, value) => {
      console.warn(
        "SierraCanvas accessed via global window property. Be aware this property doesn't exist in production and should not be relied upon."
      );
      sierraCanvas[field] = value;
    },
  });
}
